import { Modal } from "components/ui/modals/Modal/Modal"
import { BottomBar } from "components/ui/bottomBar/BottomBar"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { H3 } from "components/ui/typography/titles/Titles"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { useState } from "react"
import { ModelNumberStep } from "./layout/ModelNumberStep/ModelNumberStep"
import { PriceStep } from "./layout/PriceStep/PriceStep"
import { ButtonGetBack } from "components/ui/buttons/ButtonGetBack/ButtonGetBack"
import { useAddModelNumber } from "hooks/api/modelNumbers/modelNumbers.hook"
import { convertDataUrlToFile } from "helpers/utils.helper"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useQueryClient } from "@tanstack/react-query"
import type { TDimensions, TModelNumberPricePayloadItem } from "types/modelNumbers.type"
import style from "./AddModelNumberModal.module.scss"

const { addModelNumberModal, top, buttonsBlock, stepText, stepAndButton, bottomBar, content } = style

const initialDimensions: TDimensions = { width: [], height: [], depth: [], avg_size: "" }

type Props = { isModalOpen: boolean; setModalClose: () => void }

export const AddModelNumberModal = ({ isModalOpen, setModalClose }: Props) => {
	const [firstStep, setFirstStep] = useState(true)
	const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null)
	const [modelNumberName, setModelNumberName] = useState("")
	const [dimensions, setDimensions] = useState<TDimensions>(initialDimensions)
	const [prices, setPrices] = useState<TModelNumberPricePayloadItem[]>([])
	const [hasIncompletePrices, setHasIncompletePrices] = useState(false)
	const [showValidation, setShowValidation] = useState(false)
	const [showPriceValidation, setShowPriceValidation] = useState(false)

	const categorySelection = useCategorySelection()
	const { mutate: addModelNumberMutation } = useAddModelNumber()
	const { success: showSuccessMessage, error: showErrorMessage } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const queryClient = useQueryClient()

	const resetForm = () => {
		setFirstStep(true)
		setCroppedImageUrl(null)
		setModelNumberName("")
		categorySelection.resetAll()
		setDimensions(initialDimensions)
		setPrices([])
		setHasIncompletePrices(false)
		setShowValidation(false)
		setShowPriceValidation(false)
	}

	const closeModalHandle = () => {
		resetForm()
		setModalClose()
	}

	const hasValidDimensions =
		dimensions.width.length > 0 &&
		dimensions.height.length > 0 &&
		dimensions.depth.length > 0 &&
		dimensions.avg_size.trim() !== ""

	const isFirstStepValid = !!modelNumberName.trim() && !!croppedImageUrl && hasValidDimensions

	const onSaveHandle = () => {
		if (hasIncompletePrices) {
			setShowPriceValidation(true)
			showErrorMessage("Complete all edited price values before saving")
			return
		}

		const formData = new FormData()
		formData.append("model_number", modelNumberName)

		if (croppedImageUrl) {
			formData.append("model_number_image", convertDataUrlToFile(croppedImageUrl, "model-number-image.png"))
		}

		if (categorySelection.selectedModelCategoryId != null) {
			formData.append("related_category_id", String(categorySelection.selectedModelCategoryId))
		}

		formData.append("dimensions", JSON.stringify(dimensions))

		formData.append("prices", JSON.stringify(prices))

		addModelNumberMutation(
			{ formData },
			{
				onSuccess: () => {
					resetForm()
					setModalClose()
					showSuccessMessage("Model number added successfully")
					queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] })
				},
				onError: (error) => {
					formatError(error)
				},
			},
		)
	}

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={closeModalHandle} width="100%" closable={false}>
			<div className={addModelNumberModal}>
				<div className={top}>
					<H3>Create model number</H3>
					<button onClick={closeModalHandle} data-testid="close-modal-button">
						<CloseIcon />
					</button>
				</div>
				<div className={content}>
					{firstStep ? (
						<ModelNumberStep
							setCroppedImageUrl={setCroppedImageUrl}
							modelNumberName={modelNumberName}
							setModelNumberName={setModelNumberName}
							croppedImageUrl={croppedImageUrl}
							categorySelection={categorySelection}
							dimensions={dimensions}
							onDimensionsChange={setDimensions}
							showValidation={showValidation}
						/>
					) : (
						<PriceStep
							onPricesChange={setPrices}
							onHasIncompletePricesChange={setHasIncompletePrices}
							showValidation={showPriceValidation}
						/>
					)}
				</div>
				<BottomBar className={bottomBar}>
					<div className={buttonsBlock}>
						{firstStep ? (
							<ButtonSecondary onClick={setModalClose}>Cancel</ButtonSecondary>
						) : (
							<ButtonGetBack onClick={() => setFirstStep(true)}>Back</ButtonGetBack>
						)}
						<div className={stepAndButton}>
							<div className={stepText}>
								<span>Step {firstStep ? 1 : 2} of 2</span>
								{firstStep ? <span>Next step: Set pricing</span> : null}
							</div>
							{firstStep ? (
								<ButtonPrimary
									onClick={() => {
										setShowValidation(true)
										if (isFirstStepValid) setFirstStep(false)
									}}
								>
									Continue
								</ButtonPrimary>
							) : (
								<ButtonPrimary onClick={onSaveHandle}>Save</ButtonPrimary>
							)}
						</div>
					</div>
				</BottomBar>
			</div>
		</Modal>
	)
}
